// Grant (or re-grant) a SKU to an organisation.
// Endpoint: POST /api/admin/licensing/orgs/:org_id/skus

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  Select} from "@mui/material";
import { FormDrawer, TOKENS, useDrawerBaselineDirty } from "../design";
import { ReactComponent as GrantIcon } from "../../../../../vendor/_new/icon/services/circle_add.svg";
import { grantSku } from "../../../../../services/licensingService";
import { SafeTextField } from "../../../../common/Greens";
import { isExpiryDateBeforeToday, todayDateString } from "../../../../common/layerdDateTimePicker/utils";

const BYTE_UNITS = [
  { value: 1, label: "B" },
  { value: 1024, label: "KB" },
  { value: 1024 * 1024, label: "MB" },
  { value: 1024 * 1024 * 1024, label: "GB" },
  { value: 1024 * 1024 * 1024 * 1024, label: "TB" },
];

const SKU_OPTIONS = [
  { id: "web_security", name: "Web Security" },
  { id: "api_security", name: "API Security" },
  { id: "ai_security", name: "AI Security" },
  { id: "mcp_security", name: "MCP Security" },
];

const addDaysIso = (n) => {
  const d = new Date(Date.now() + n * 86400000);
  return d.toISOString().slice(0, 10); // yyyy-mm-dd
};

const fieldLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: TOKENS.fg2,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.5,
};

const helperSx = { fontSize: 11.5, color: TOKENS.fg3, mt: 0.5 };

const M_GrantSku = ({
  open,
  orgId,
  orgTitle,
  presetSku,         // catalog row when invoked from a per-SKU card; null = picker enabled
  availableSkus = [],
  onClose,
  onSaved,
}) => {
  const [skuId, setSkuId] = useState("");
  const [isTrial, setIsTrial] = useState(false);
  const [expiry, setExpiry] = useState(addDaysIso(365));
  const [reqLimit, setReqLimit] = useState(""); // blank = unlimited
  const [byteLimit, setByteLimit] = useState("");
  const [byteUnit, setByteUnit] = useState(1024 * 1024 * 1024); // GB
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [baselineKey, setBaselineKey] = useState(0);

  // Reset state when the drawer opens.
  useEffect(() => {
    if (!open) return;
    setSkuId(presetSku?.id || (availableSkus[0] && availableSkus[0].id) || "");
    setIsTrial(false);
    setExpiry(addDaysIso(365));
    setReqLimit("");
    setByteLimit("");
    setByteUnit(1024 * 1024 * 1024);
    setNotes("");
    setSaving(false);
    setErr(null);
    setBaselineKey((k) => k + 1);
  }, [open, presetSku, availableSkus]);

  // When trial is toggled on, default the expiry to +14d (if user hasn't picked a custom value yet).
  useEffect(() => {
    if (isTrial) setExpiry(addDaysIso(14));
  }, [isTrial]);

  const pickerOptions = useMemo(() => {
    // If a preset was supplied (card click), lock that SKU.
    if (presetSku) return [{ id: presetSku.id, name: presetSku.name || presetSku.id }];
    // Else union of availableSkus + the 3 known ones (so we never end up
    // with an empty picker when the backend response is missing for some reason).
    if (availableSkus.length) return availableSkus.map((s) => ({ id: s.id, name: s.name || s.id }));
    return SKU_OPTIONS;
  }, [presetSku, availableSkus]);

  const formSnapshot = useMemo(
    () => ({ skuId, isTrial, expiry, reqLimit, byteLimit, byteUnit, notes }),
    [skuId, isTrial, expiry, reqLimit, byteLimit, byteUnit, notes],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);

  const canSubmit =
    !!skuId &&
    !!expiry &&
    !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (isExpiryDateBeforeToday(expiry)) {
      setErr("Expiry date must be today or in the future");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const body = {
        sku_id: skuId,
        is_trial: isTrial,
        // Send midnight UTC ISO.
        expiry_date: new Date(`${expiry}T00:00:00.000Z`).toISOString(),
        notes: notes.trim() || null,
      };
      const r = reqLimit === "" ? null : Number(reqLimit);
      const b = byteLimit === "" ? null : Number(byteLimit) * Number(byteUnit);
      body.volume_limit_requests = r === null ? null : r;
      body.volume_limit_bytes = b === null ? null : b;
      await grantSku(orgId, body);
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to grant SKU");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={presetSku ? `Grant ${presetSku.name || presetSku.id}` : "Grant SKU"}
      subtitle="Assign an SKU to this organisation"
      icon={<GrantIcon />}
      submitLabel="Grant"
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      loading={saving}
      isDirty={isDirty}
    >
      <Stack spacing={2.5}>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
          Granting to <strong>{orgTitle}</strong>. UPSERT — re-issuing a SKU resets its expiry, quotas, and trial flag.
        </Typography>

        <Box>
          <Typography sx={fieldLabelSx}>SKU</Typography>
          <Select
            value={skuId}
            onChange={(e) => setSkuId(e.target.value)}
            disabled={!!presetSku}
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-input": { fontSize: 13 } }}
          >
            {pickerOptions.map((o) => (
              <MenuItem key={o.id} value={o.id} sx={{ fontSize: 13 }}>{o.name}</MenuItem>
            ))}
          </Select>
        </Box>

        <FormControlLabel
          sx={{ ml: 0, "& .MuiFormControlLabel-label": { fontSize: 13, color: TOKENS.fg1 } }}
          control={<Switch size="small" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />}
          label="Trial grant (defaults expiry to today + 14 days)"
          labelPlacement="end"
        />

        <Box>
          <Typography sx={fieldLabelSx}>Expiry date</Typography>
          <SafeTextField
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            size="small"
            fullWidth
            inputProps={{ min: todayDateString() }}
            InputProps={{ sx: { fontSize: 13 } }}
          />
          <Typography sx={helperSx}>Stored as midnight UTC on the selected day.</Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Request quota</Typography>
          <SafeTextField
            type="number"
            value={reqLimit}
            onChange={(e) => setReqLimit(e.target.value)}
            placeholder="Unlimited"
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
            InputProps={{ sx: { fontSize: 13 } }}
          />
          <Typography sx={helperSx}>Blank = unlimited. Otherwise total requests allowed in the billing window.</Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Byte quota</Typography>
          <Stack direction="row" spacing={1}>
            <SafeTextField
              type="number"
              value={byteLimit}
              onChange={(e) => setByteLimit(e.target.value)}
              placeholder="Unlimited"
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ min: 0.0001, step: "any" }}
              InputProps={{ sx: { fontSize: 13 } }}
            />
            <Select
              value={byteUnit}
              onChange={(e) => setByteUnit(Number(e.target.value))}
              size="small"
              sx={{ minWidth: 84, "& .MuiOutlinedInput-input": { fontSize: 13 } }}
              disabled={byteLimit === ""}
            >
              {BYTE_UNITS.map((u) => (
                <MenuItem key={u.label} value={u.value} sx={{ fontSize: 13 }}>{u.label}</MenuItem>
              ))}
            </Select>
          </Stack>
          <Typography sx={helperSx}>Blank = unlimited. Stored in bytes after multiplying the unit.</Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Notes</Typography>
          <SafeTextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional — e.g. PO number, contract reference, contact"
            size="small"
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 2048 }}
            InputProps={{ sx: { fontSize: 13 } }}
          />
        </Box>

        {err ? (
          <Box
            sx={{
              p: 1.25,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.dangerBg,
              border: `1px solid ${TOKENS.dangerBorder}`,
              color: TOKENS.dangerInk,
              fontSize: 12.5,
            }}
          >
            {err}
          </Box>
        ) : null}
      </Stack>
    </FormDrawer>
  );
};

export default M_GrantSku;
